/* ═══════════════════════════════════════════════════════════════
 * ATR GRID — horizontale prijsniveaus op kwart-ATR-afstand (Wyckoff-
 * opzet uit atr_grid.py). 1:1 poort van TERMINAL.atrGrid uit
 * terminal_wyckoffpcg.html.
 *
 *   step   = quarter * ATR         (quarter = 0.25)
 *   level  = anchor + n*step
 *   100%   = elke volle ATR (n veelvoud van 4)
 *
 * Anchor + ATR-unit zijn bewust vastgezet voor het geconfigureerde
 * symbool (locked config, zie config.js → atrGrid.symbol/anchor/atr):
 * op grote afstand zou live-drift de bovenkant honderden dollars
 * verschuiven. Voor andere symbolen valt hij terug op live
 * Wilder-ATR + diepste swing-low (berekend op dagcandles, timeframe-
 * onafhankelijk). Alleen de niveaus binnen een ATR-venster rond de
 * prijs worden getekend.
 * ═══════════════════════════════════════════════════════════════ */

import { CONFIG as CFG } from "./config";
import { fetchKlines } from "./binance";

function trueRanges(cs) {
  const tr = [];
  for (let i = 0; i < cs.length; i++) {
    const c = cs[i];
    if (i === 0) { tr.push(c.high - c.low); continue; }
    const p = cs[i - 1].close;
    tr.push(Math.max(c.high - c.low, Math.abs(c.high - p), Math.abs(c.low - p)));
  }
  return tr;
}

// Wilder ATR (ewm adjust=False, seed op TR[0])
function atrWilder(cs, period) {
  const tr = trueRanges(cs);
  if (!tr.length) return NaN;
  const a = 1 / period;
  let v = tr[0];
  for (let i = 1; i < tr.length; i++) v = (1 - a) * v + a * tr[i];
  return v;
}

// Diepste swing-low (lokaal minimum over ±window) = macro-bodem
function deepestSwingLow(cs, window) {
  let best = null;
  for (let i = window; i < cs.length - window; i++) {
    const lo = cs[i].low;
    let isLow = true;
    for (let j = i - window; j <= i + window; j++) { if (cs[j].low < lo) { isLow = false; break; } }
    if (isLow && (best === null || lo < best)) best = lo;
  }
  if (best === null) { best = Infinity; for (const c of cs) if (c.low < best) best = c.low; }
  return best;
}

// getApi: () => { chart, candleSeries }
// getState: () => { candles, symbol }
export function createAtrGrid(getApi, getState) {
  let lines = [];
  let on = false;
  const daily = { symbol: null, atr: NaN, anchor: NaN }; // cache voor live-symbolen
  const series = () => getApi().candleSeries;

  function clear() {
    const s = series();
    lines.forEach((l) => { try { s.removePriceLine(l); } catch {} });
    lines = [];
  }

  async function ensureDaily(symbol) {
    if (daily.symbol === symbol && daily.atr > 0) return;
    const cfg = CFG.atrGrid;
    try {
      const dc = await fetchKlines(symbol, "1d", 500);
      if (dc && dc.length) {
        daily.symbol = symbol;
        daily.atr = atrWilder(dc, cfg.period || 14);
        daily.anchor = deepestSwingLow(dc, cfg.swingWindow || 15);
      }
    } catch {
      /* val terug op de huidige candles hieronder */
    }
  }

  function visibleRange(candles) {
    try {
      const vr = getApi().chart.timeScale().getVisibleLogicalRange();
      if (!vr) return null;
      const from = Math.max(0, Math.floor(vr.from)), to = Math.min(candles.length - 1, Math.ceil(vr.to));
      let lo = Infinity, hi = -Infinity;
      for (let i = from; i <= to; i++) { if (candles[i].low < lo) lo = candles[i].low; if (candles[i].high > hi) hi = candles[i].high; }
      return (lo < hi) ? { lo, hi } : null;
    } catch {
      return null;
    }
  }

  async function compute() {
    clear();
    if (!on) return;
    const st = getState();
    const candles = st.candles;
    if (!candles || !candles.length) return;
    const cfg = CFG.atrGrid;
    const price = candles[candles.length - 1].close;

    let anchor, atrUnit;
    const locked = cfg.symbol && st.symbol === cfg.symbol && cfg.anchor > 0 && cfg.atr > 0;
    if (locked) {
      anchor = cfg.anchor; atrUnit = cfg.atr;
    } else {
      await ensureDaily(st.symbol);
      if (!on || getState().symbol !== st.symbol) return; // symbool/toggle veranderd tijdens fetch
      if (daily.symbol === st.symbol && daily.atr > 0) { atrUnit = daily.atr; anchor = daily.anchor; }
      else { atrUnit = atrWilder(candles, cfg.period || 14); anchor = deepestSwingLow(candles, cfg.swingWindow || 15); }
    }
    if (!(atrUnit > 0)) return;

    const q = cfg.quarter || 0.25;
    const step = atrUnit * q;
    const bandAtr = cfg.band || 4;
    let lo = price - bandAtr * atrUnit, hi = price + bandAtr * atrUnit;
    const vis = visibleRange(candles);
    if (vis) { const pad = 0.5 * atrUnit; lo = Math.min(lo, vis.lo - pad); hi = Math.max(hi, vis.hi + pad); }

    const tol = cfg.confTol > 0 ? cfg.confTol : atrUnit * q / 2;
    const struct = (locked && cfg.showStructural) ? (cfg.structural || {}) : {};
    const s = series();

    const nLo = Math.floor((lo - anchor) / step);
    const nHi = Math.ceil((hi - anchor) / step);
    let count = 0;
    const MAX = 200;
    for (let n = nLo; n <= nHi && count < MAX; n++) {
      const level = anchor + n * step;
      if (level < lo || level > hi) continue;
      const mult = n * q;
      const frac = ((mult % 1) + 1) % 1;
      const isFull = Math.abs(frac) < 1e-9;
      const isHalf = Math.abs(frac - 0.5) < 1e-9;
      if (!cfg.showQuarters && !isFull && !isHalf) continue;

      const confHits = [];
      for (const k in struct) { if (Math.abs(level - struct[k]) <= tol) confHits.push(k); }
      const confName = confHits.join(", ");

      let color, lineStyle, title, labelVisible;
      if (confName) { color = cfg.colConf; lineStyle = 0; title = confName; labelVisible = true; }
      else if (isFull) { color = cfg.colFull; lineStyle = 0; title = Math.round(mult) + " ATR"; labelVisible = true; }
      else if (isHalf) { color = cfg.colHalf; lineStyle = 2; title = "½"; labelVisible = !!cfg.labelHalf; }
      else { color = cfg.colQuarter; lineStyle = 1; title = ""; labelVisible = false; }

      lines.push(s.createPriceLine({ price: level, color, lineWidth: 1, lineStyle, axisLabelVisible: labelVisible, title }));
      count++;
    }
  }

  return {
    setVisible: (v) => { on = v; compute(); },
    isOn: () => on,
    recompute: () => { if (on) compute(); },
    clear,
  };
}
